<template>
  <v-card
    class="member-card ma-2"
    :class="{ dragging: isDragging }"
    draggable="true"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    elevation="2"
    outlined
  >
    <v-card-text class="pb-2">
      <div class="d-flex align-center justify-space-between">
        <!-- Member Avatar and Info -->
        <div class="d-flex align-center">
          <v-avatar
            :color="member.avatar ? 'transparent' : 'primary'"
            size="40"
            class="mr-3"
          >
            <v-img
              v-if="member.avatar"
              :src="member.avatar"
              :alt="member.name"
            />
            <span v-else class="white--text headline">
              {{ getInitials(member.name) }}
            </span>
          </v-avatar>

          <div>
            <div class="font-weight-medium">{{ member.name }}</div>
            <div class="text-caption text--secondary">{{ member.email }}</div>
            <v-chip
              v-if="member.role"
              x-small
              :color="getRoleColor(member.role)"
              text-color="white"
              class="mt-1"
            >
              {{ member.role }}
            </v-chip>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex align-center">
          <v-btn
            icon
            small
            @click="openEditDialog"
            color="primary"
            title="Edit Member"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>

          <!-- Exit from current group button (only show if not in pool) -->
          <v-btn
            v-if="!isPool && currentGroupId"
            icon
            small
            @click="confirmRemoveFromGroup"
            color="warning"
            title="Remove from this group"
          >
            <v-icon small>mdi-exit-to-app</v-icon>
          </v-btn>

          <v-btn
            icon
            small
            @click="confirmDelete"
            color="error"
            title="Delete member completely"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>

          <!-- Drag Handle -->
          <v-icon class="drag-handle ml-1" color="grey" small>
            mdi-drag-vertical
          </v-icon>
        </div>
      </div>

      <!-- Additional Member Info -->
      <div
        v-if="member.department || member.joinDate || memberGroups.length"
        class="mt-2"
      >
        <v-chip v-if="member.department" x-small outlined class="mr-1">
          {{ member.department }}
        </v-chip>

        <!-- Show all groups this member belongs to -->
        <div v-if="memberGroups.length" class="mb-1">
          <v-tooltip v-for="group in memberGroups" :key="group.id" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                x-small
                :color="group.id == currentGroupId ? 'primary' : 'secondary'"
                :outlined="group.id != currentGroupId"
                class="mr-1 mb-1"
              >
                <v-icon x-small left>mdi-account-group</v-icon>
                {{ group.name }}
              </v-chip>
            </template>
            <span>
              {{ group.name }}
              <br v-if="group.emis" />
              <small v-if="group.emis">EMIS: {{ group.emis }}</small>
            </span>
          </v-tooltip>
        </div>

        <span v-if="member.joinDate" class="text-caption text--secondary">
          Joined: {{ formatDate(member.joinDate) }}
        </span>
      </div>
    </v-card-text>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="500px">
      <AddEditMember
        :groups="groups"
        :editedMember="editedMember"
        @close-dialog="closeDialog()"
        @save-member="saveMember"
      ></AddEditMember>
      <!-- <v-card>
        <v-card-title>
          <span class="headline">Edit Member</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedMember.name"
                  label="Name"
                  required
                  :rules="nameRules"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-if="member"
                  :readonly="member.table == 'users'"
                  v-model="editedMember.email"
                  label="Email [Optional]"
                  type="email"
                  :rules="emailRules"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="editedMember.role"
                  :items="roleOptions"
                  label="Role In First Group [Required]"
                  item-text="text"
                  item-value="value"
                  return-object
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedMember.id_number"
                  label="ID Number [Optional]"
                />
              </v-col>

              <v-col cols="12">
                <v-select
                  chips
                  v-model="editedMember.groups"
                  :items="groups"
                  label="Groups"
                  item-text="name"
                  item-value="id"
                  multiple
                  return-object
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" text @click="editDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            @click="saveMember"
            :disabled="!isFormValid"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card> -->
    </v-dialog>

    <!-- Remove from Group Confirmation Dialog -->
    <v-dialog v-model="removeFromGroupDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Remove from Group</v-card-title>
        <v-card-text>
          Are you sure you want to remove
          <strong>{{ member.name }}</strong> from
          <strong>{{ getCurrentGroupName() }}</strong
          >? <br /><br />
          <em
            >Note: The member will remain in other groups and in the members
            pool.</em
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="removeFromGroupDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn color="orange darken-1" text @click="handleRemoveFromGroup">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Delete Member</v-card-title>
        <v-card-text>
          Are you sure you want to <strong>permanently delete</strong>
          <strong>{{ member.name }}</strong
          >? <br /><br />
          <em>This will remove them from all groups and cannot be undone.</em>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" text @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="red darken-1" text @click="handleDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import AddEditMember from "./AddEditMember.vue";
export default {
  name: "MemberCard",
  components: { AddEditMember },
  props: {
    groups: {
      type: Array,
      required: true,
      default: () => [],
    },
    member: {
      type: Object,
      required: true,
      validator: (member) => {
        return member && member.id && member.name;
      },
    },
    currentGroupId: {
      type: [String, Number],
      default: null,
    },
    isPool: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    memberGroups() {
      if (!this.member.groups || !Array.isArray(this.member.groups)) {
        return [];
      }

      // If member.groups contains objects, return them directly
      // If member.groups contains IDs, map them to group objects
      return this.member.groups
        .map((group) => {
          if (typeof group === "object" && group.id) {
            // Already a group object
            return group;
          } else {
            // Assume it's a group ID, find the corresponding group object
            const groupId = typeof group === "object" ? group.id : group;
            return (
              this.groups.find((g) => g.id == groupId) || {
                id: groupId,
                name: "Unknown Group",
                emis: null,
              }
            );
          }
        })
        .filter((group) => group);
    },

    memberGroupIds() {
      // Helper computed property to get just the group IDs
      return this.memberGroups.map((group) => group.id);
    },
  },
  data() {
    return {
      isDragging: false,
      editDialog: false,
      deleteDialog: false,
      editedMember: {},
      removeFromGroupDialog: false,
    };
  },

  methods: {
    handleDragStart(e) {
      this.isDragging = true;

      // Set drag data with member info and current groups
      const dragData = {
        memberId: this.member.id,
        memberName: this.member.name,
        member: this.member,
        currentGroups: this.memberGroups, // Full group objects
        currentGroupIds: this.memberGroupIds, // Just the IDs for easy checking
        fromGroupId: this.currentGroupId,
        isFromPool: this.isPool,
      };

      e.dataTransfer.setData("text/plain", JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = "copy"; // Changed to copy since we're leaving copies

      // Emit drag start event to parent
      this.$emit("drag-start", {
        event: e,
        member: this.member,
        dragData: dragData,
      });
    },

    handleDragEnd() {
      this.isDragging = false;
    },

    openEditDialog() {
      this.editedMember = { ...this.member };
      this.editDialog = true;
    },

    saveMember(editedMember) {
      this.$emit("update-member", {
        ...editedMember,
      });
      this.closeDialog();
    },

    confirmDelete() {
      this.deleteDialog = true;
    },

    handleDelete() {
      this.$emit("delete-member", this.member.id);
      this.deleteDialog = false;
    },

    getInitials(name) {
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },

    getRoleColor(role) {
      const colors = {
        owner: "purple",
        admin: "red",
        moderator: "orange",
        member: "blue",
      };
      return colors[role] || "grey";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    confirmRemoveFromGroup() {
      this.removeFromGroupDialog = true;
    },

    handleRemoveFromGroup() {
      this.$emit("remove-from-group", {
        memberId: this.member.id,
        groupId: this.currentGroupId,
      });
      this.removeFromGroupDialog = false;
    },

    getCurrentGroupName() {
      if (!this.currentGroupId) return "this group";
      const group = this.groups.find((g) => g.id == this.currentGroupId);
      return group ? group.name : "this group";
    },
    closeDialog() {
      this.editDialog = false;
    },
  },
};
</script>

<style scoped>
.member-card {
  transition: all 0.3s ease;
  cursor: grab;
}

.member-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.member-card.dragging {
  opacity: 0.6;
  transform: rotate(5deg);
  cursor: grabbing;
}

.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.member-card:active {
  cursor: grabbing;
}
</style>
